/**
 * Résolution de l'URL de front5 (v5) depuis front4.
 *
 * En production, l'URL de base provient de la configuration backend (`appConfig.config.V5.url`)
 * et front5 est servi sur la même origine (ex. « /front5 »).
 * En développement, front4 (:7000) et front5 (:5000) sont sur des origines distinctes :
 * on force donc l'URL absolue du serveur de dev front5, afin que la navigation aille
 * réellement sur « http://localhost:5000/front5/... » (et non via un proxy sur :7000).
 *
 * Aligné sur la logique de `~/components/v5-frame/v5-frame.controller.js`.
 */

/**
 * Retourne l'URL de base de front5, toujours terminée par « / ».
 *
 * @param {string | undefined} configuredUrl Valeur de `appConfig.config.V5?.url`
 * @returns {string} URL de base de front5
 */
export const getV5BaseUrl = (configuredUrl = '/front5') => {
	// `process.env.NODE_ENV` et `process.env.FRONT5` sont injectés au build par webpack (DefinePlugin).
	// En dev, on utilise la variable d'environnement FRONT5 (cf. .env) plutôt qu'une URL en dur.
	// eslint-disable-next-line no-undef
	const isProduction = process.env.NODE_ENV === 'production';
	// eslint-disable-next-line no-undef
	let baseUrl = isProduction ? configuredUrl : (process.env.FRONT5 ?? 'http://localhost:5000/front5');

	if (!baseUrl.endsWith('/')) {
		baseUrl += '/';
	}

	return baseUrl;
};

/**
 * Construit une URL absolue vers une route front5.
 *
 * @param {string | undefined} configuredUrl Valeur de `appConfig.config.V5?.url`
 * @param {string} path Route front5 sans slash initial (ex. « support-request »)
 * @param {Record<string, string>} [parameters] Paramètres de query éventuels
 * @returns {string} URL absolue prête pour une navigation
 */
export const buildV5Url = (configuredUrl, path, parameters = {}) => {
	const baseUrl = getV5BaseUrl(configuredUrl);

	const url = baseUrl.startsWith('http')
		? new URL(`${baseUrl}${path}`)
		: new URL(`${baseUrl}${path}`, window.location.origin);

	for (const [key, value] of Object.entries(parameters)) {
		url.searchParams.set(key, value);
	}

	return url.href;
};
